import dayjs, { Dayjs } from "dayjs";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetMasterConfig } from "../../../../../../../api/v1/useGetMasterConfig";
import { LeaseTerms } from "../../../../../../../types/bookings";
import { SelectOption } from "../../../../../../../types/select";
import { findSelectOption } from "../../../../../../libs/utils";
import { LeaseEndDateRefType } from "../../../NumberOfYearsSelect";
import ResidentLeaseTermsForm from "../../../ResidentLeaseTermsForm";
import { apiDateFormat, dateFormat, generateYearOptions, lease_nature, rentFreePeriodValues } from "../../../utils";
import { getDefaultValues } from "../../utili";

export type LeaseTermFormData = {
  leaseManager?: string;
  externalAgent?: string;
  leaseNature?: SelectOption;
  numberOfOcupants?: string;
  bookingStartDate?: string | Dayjs;
  bookingEndDate?: string | Dayjs;
  fitOutStartDate?: string | Dayjs;
  fitOutEndDate?: string | Dayjs;
  leaseStartDate?: string | Dayjs;
  leaseEndDate?: string | Dayjs;
  numberOfYears: SelectOption;
  rentFreePeriod?: SelectOption;
  moveInDate?: string | Dayjs;
  parkings?: unknown[];
  noOfFreeParkingSlots: number;
};

export interface LeaseTermFormHandle {
  submitForm: () => void;
}

interface LeaseTermFormProps {
  step: String;
  onSubmit: (body: any) => void;
}

export const LeaseTermForm = forwardRef<LeaseTermFormHandle, LeaseTermFormProps>(({ step, onSubmit }, ref) => {
  const { bookingForm, landlordUsers, bookingDetails } = useSelector((state: any) => state.newBooking);

  const leaseEndDateRef = useRef<LeaseEndDateRefType>({ actual: null, withRentFreePeriod: null });
  const { leaseId = bookingDetails.leaseId } = useParams();
  const yearsOptions = generateYearOptions({ start: 1, end: 10, withCustomOption: true });
  const rentFreePeriodOptions = rentFreePeriodValues() as SelectOption[];
  // Initialize default dates

  const getInitialBookingDates = () => {
    const { bookingStartDate, bookingEndDate, moveInDate } = bookingForm?.bookingTerms || {};
    const { leaseStartDate, leaseEndDate } = (bookingDetails?.leaseTerms || {}) as LeaseTerms;

    const now = dayjs();
    const defaultStart = now.add(14, "day");

    const initialLeaseEndDate = dateFormat(leaseEndDate) || defaultStart.add(1, "year").subtract(1, "day");

    return {
      initialBookingStartDate: dateFormat(bookingStartDate) || now,
      initialBookingEndDate: dateFormat(bookingEndDate) || defaultStart,
      initialLeaseStartDate: dateFormat(leaseStartDate) || defaultStart,
      initialLeaseEndDate,
      initialRentFreeStartDate: initialLeaseEndDate,
      initialMoveInDate: dateFormat(moveInDate) || defaultStart,
    };
  };

  const {
    initialBookingStartDate,
    initialBookingEndDate,
    initialLeaseStartDate,
    initialLeaseEndDate,
    initialRentFreeStartDate,
    initialMoveInDate,
  } = getInitialBookingDates();

  const methods = useForm<LeaseTermFormData>({
    defaultValues: getDefaultValues(
      bookingDetails,
      bookingForm,
      lease_nature,
      landlordUsers,
      initialBookingStartDate,
      initialBookingEndDate,
      initialLeaseStartDate,
      initialLeaseEndDate,
      yearsOptions,
      initialMoveInDate,
      rentFreePeriodOptions,
    ) satisfies LeaseTermFormData,
  });

  const { control, watch, getValues, setValue, setError, handleSubmit, reset, formState } = methods;
  const { errors } = formState;
  const watchedFormValues = watch();

  const { masterConfig, isSuccessMasterConfig } = useGetMasterConfig({
    configItem: "leaseNature",
    filterBy: "usageType",
    value: "residential",
  });

  const leaseNatureOptions: SelectOption[] = masterConfig
    ? Object.entries(masterConfig)?.map(([value, label]) => ({ value, label }))
    : [];

  // Reset form when bookingForm changes
  useEffect(() => {
    if (bookingForm) {
      reset(
        getDefaultValues(
          bookingDetails,
          bookingForm,
          lease_nature,
          landlordUsers,
          initialBookingStartDate,
          initialBookingEndDate,
          initialLeaseStartDate,
          initialLeaseEndDate,
          yearsOptions,
          initialMoveInDate,
          rentFreePeriodOptions,
        ),
      );
    }
  }, [bookingForm]);

  useEffect(() => {
    if (!bookingDetails?.leaseTerms?.leaseNature || !isSuccessMasterConfig) return;

    const selectedLeaseNature = findSelectOption(leaseNatureOptions, bookingDetails.leaseTerms.leaseNature);
    if (!selectedLeaseNature) return;

    setValue("leaseNature", selectedLeaseNature);
  }, [bookingDetails?.leaseTerms?.leaseNature, isSuccessMasterConfig]);
  // Prepare final payload and send to parent
  const submitHandler = handleSubmit((payload) => {
    const dataCopy = { ...(payload || getValues()) };
    const body = {
      leaseId,
      externalAgent: dataCopy.externalAgent,
      leaseManager: dataCopy.leaseManager["value"],
      numberOfOcupants: dataCopy.numberOfOcupants,
      leaseTerms: {
        leaseNature: dataCopy.leaseNature["value"],
        moveInDate: apiDateFormat(dataCopy.moveInDate),
        leaseEndDate: apiDateFormat(dataCopy.leaseEndDate),
        leaseStartDate: apiDateFormat(dataCopy.leaseStartDate),
        numberOfYears: dataCopy.numberOfYears?.value,
        noOfFreeParkingSlots: Number(dataCopy.noOfFreeParkingSlots) || 0,
        rentFreePeriod: dataCopy?.rentFreePeriod?.value,
        ...(parseInt(dataCopy.rentFreePeriod?.value) > 0
          ? {
              rentFreeStartDate: apiDateFormat(leaseEndDateRef.current.actual),
              rentFreeEndDate: apiDateFormat(leaseEndDateRef.current.withRentFreePeriod),
            }
          : {}),
      },
      bookingTerms: {
        bookingStartDate: apiDateFormat(dataCopy.bookingStartDate),
        bookingEndDate: apiDateFormat(dataCopy.bookingEndDate),
      },
    };
    onSubmit(body);
  });

  // Expose submit method to parent
  useImperativeHandle(ref, () => ({
    submitForm: () => submitHandler(),
  }));

  return (
    <>
      <ResidentLeaseTermsForm
        setValue={setValue}
        watchedFormValues={watchedFormValues}
        control={control}
        getValues={getValues}
        errors={errors}
        setError={setError}
        methods={methods}
        onSubmit={submitHandler}
        handleSubmit={handleSubmit}
        parkingsWatcher={null}
        leaseEndDateRef={leaseEndDateRef}
        isNumberOfYear={true}
      />
    </>
  );
});
